import {NgxTranslation, NgxTranslationOptions} from '@ngx-translations/translations.interfaces';
import {Inject, Injectable, Optional, PLATFORM_ID} from '@angular/core';
import {NgxStorage} from '@ngx-storage/storage.service';
import {isPlatformBrowser} from '@angular/common';
import {Meta} from '@angular/platform-browser';
import {BehaviorSubject} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class NgxTranslations {

    // The token used in the storage.
    private readonly _storageToken = '__NGX_CURRENT_LANGUAGE__';

    // The available languages.
    private readonly _dictionary = [];

    // The default dictionary.
    private readonly _defaultDictionary = [{
        languages: ['en-US', 'en'],
        locale: 'en-US',
        data: null,
        name: 'English (English)'
    }];

    // The current language.
    private _currentLanguage$ = new BehaviorSubject<string>(null);

    // Warn function.
    private warn = (text: string) => {
        if (console && console.warn) {
            console.warn(text);
        }
    };

    constructor(private _meta: Meta,
                @Optional() private _storage: NgxStorage,
                @Inject(PLATFORM_ID) private _platformId: object,
                @Optional() private _config: NgxTranslationOptions) {
        // Retrieve the dictionary from the config.
        if (!this._config) {
            this._config = {production: false, dictionary: []};
        }

        // Append the dictionary to the default dictionary.
        this._dictionary = this._config.dictionary.concat(
            this._defaultDictionary
        );

        // Check if the languages are all correct if the application is not in production mode.
        if (!this._config.production && !this.validate()) {
            this.warn('The language files have been compared and they have not passed the validation.');
        }

        // If the storage is enabled than retrieve the current language; else us an empty behavior subject.
        const language$ = this._storage ?
            this._storage.get<string>(this._storageToken) as BehaviorSubject<string>
            : new BehaviorSubject<string>(null);
        const language = language$.getValue();

        // Verify the language.
        let verifiedLanguage = this.verify(language);

        // Get the accept languages from the meta tag.
        const acceptLanguages = this.parseAcceptLanguages();

        for (let index = 0; index < acceptLanguages.length; index++) {
            const acceptLanguage = acceptLanguages[index];
            let language = acceptLanguage.code;
            language += acceptLanguage.region ? '-' + acceptLanguage.region : '';

            if (this.isLanguageSupported(language)) {
                verifiedLanguage = language;
                break;
            }
        }

        // Use the verified language.
        this.use(verifiedLanguage);
    }

    use(language: string) {
        // Check if the language is supported; if not set the default language and warn the user.
        if (!this.isLanguageSupported(language)) {
            language = this._defaultDictionary[0].languages[0];
            this.warn(
                `The language is not supported; The language has been set to '${language}'`
            );
        }

        // Submit a new language.
        this._currentLanguage$.next(language);

        // Save the new language in the storage if the storage exists.
        if (this._storage) {
            this._storage.set<string>(this._storageToken, language);
        }
    }

    instant(data: string | NgxTranslation) {
        return this.translate(data);
    }

    translate(item: string | NgxTranslation) {
        // Retrieve the entire dictionary object.
        const dictionary = this.getLanguageDictionary(this._currentLanguage$.getValue());

        // Get the token.
        const token = typeof item === 'object' ? item.token : item;
        const data = typeof item === 'object' ? item.data : {};
        const shouldTranslate = typeof item === 'object' && item.translate !== undefined ? item.translate : true;

        // Check the compiler should translate; else return the token.
        if (!shouldTranslate) {
            return item['token'];
        }

        // Get the sentence.
        let sentence = null;

        // Check if the dictionary exists.
        if (dictionary) {
            if (!dictionary[token]) {
                // The key does not exist in the dictionary, so the user must be notified.
                this.notify(`Key '${token}' was not found; ${token} was returned.`);
                sentence = token;
            } else {
                // The key does exist, so return it.
                sentence = dictionary[token];
            }
        } else {
            // The language is the default.
            sentence = token;
        }

        // Loop through each match to replace it with the supplied value.
        (sentence.match(/\${(.*?)}/g) || []).forEach(match => {
            // Get the key without any other symbols.
            const key = match.replace(/[${} ]/g, '');

            // Split the key on the pipe symbol to get the other arguments.
            const words = key.split('|');
            if (words.length <= 0 || words.length > 2) {
                return;
            }

            // Get the key.
            let word = words[0];

            // Check if it should be translated (default is yes).
            if (data[word]) {
                word = data[word];

                if (words.length === 1 || (words[1] && words[1].split(',').indexOf('keep') === -1)) {
                    word = this.translate(word);
                }
            }

            // Do the other attributes.
            if (words.length === 2) {
                const actions = words[1].split(',');

                actions.forEach(action => {
                    if (action === 'lowercase') {
                        word = word.toLowerCase();
                    } else if (action === 'uppercase') {
                        word = word.toUpperCase();
                    } else if (action === 'ucfirst') {
                        const character = word.charAt(0).toUpperCase();
                        word = character + word.substr(1);
                    }
                });
            }

            sentence = sentence.replace(match, word);
        });

        return sentence;
    }

    getLanguage() {
        return this._currentLanguage$.getValue();
    }

    getDictionary() {
        return this._dictionary;
    }

    getLocale() {
        return this.getLanguageLocale(this.getLanguage());
    }

    private verify(language: string) {
        // Check if the language already exists.
        if (language !== null && this.isLanguageSupported(language)) {
            return language;
        }

        // Fetch the browser language.
        let browserLanguage = this.getBrowserLanguage();

        // Check if the browsers language is supported in the system.
        if (!this.isLanguageSupported(browserLanguage)) {
            browserLanguage = this._defaultDictionary[0].languages[0];
        }

        // If the browser language was not supported put one in the storage.
        if (this._storage) {
            this._storage.set<string>(this._storageToken, browserLanguage);
        }

        // Return the browser language.
        return browserLanguage;
    }

    private getBrowserLanguage() {
        let language = null;

        // Check if the platform is browser.
        if (!isPlatformBrowser(this._platformId)) {
            return language;
        }

        // Check if the navigator exists.
        if (!navigator) {
            return language;
        }

        // Get the correct first language of the browser.
        if (navigator.languages && navigator.languages.length) {
            language = navigator.languages[0];
        } else if (navigator['userLanguage']) {
            language = navigator['userLanguage'];
        } else {
            language = navigator.language;
        }

        return language;
    }

    private getLanguageDictionary(language: string) {
        for (let item of this._dictionary) {
            if (item.languages.indexOf(language) > -1) {
                return item.data;
            }
        }

        return null;
    }

    private getLanguageLocale(language: string) {
        for (let item of this._dictionary) {
            if (item.languages.indexOf(language) > -1) {
                return item.locale;
            }
        }

        return null;
    }

    private isLanguageSupported(language: string) {
        for (let item of this._dictionary) {
            if (item.languages.indexOf(language) > -1) {
                return true;
            }
        }

        return false;
    }

    private validate() {
        const array = [];

        this._dictionary.forEach(language => {
            if (!language.data) {
                return;
            }

            const data = this.getKeys(language.data);
            array.push(data);
        });

        const first = array[0] || [];

        return array.every(element => this.compare(first, element));
    }

    private getKeys(object: object, identifier: string = null) {
        const result = [];

        Object.keys(object).forEach(key => {
            const item = object[key];

            if (typeof item === 'object') {
                result.push(...this.getKeys(item, identifier ? (identifier + '.' + key) : key));
            } else {
                result.push(identifier ? (identifier + '.' + key) : key);
            }
        });

        return result;
    }

    private compare(first: string[], second: string[]) {
        if (first === second) {
            return true;
        }

        if (first === null || second === null) {
            return false;
        }

        let isValid = true;

        if (first.length !== second.length) {
            this.warn('The files have been compared and not every file has the same length.');
            isValid = false;
        }

        for (let index = 0; index < first.length; ++index) {
            if (first[index] !== second[index]) {
                isValid = false;
                this.warn(`Line ${index} did not equal the first language.json file; ${first[index]} !== ${second[index]}`);
            }
        }

        return isValid;
    }

    private getAcceptLanguages() {
        // Get the meta tag from the document.
        const tag = this._meta.getTag('name=ngx-accept-languages');

        // Check if the tag exists, if so return the content.
        return tag ? tag.content : null;
    }

    private parseAcceptLanguages() {
        // Get the accept languages.
        const data = this.getAcceptLanguages();

        // Parse them.
        const strings = (data || '')
            .match(/((([a-zA-Z]+(-[a-zA-Z0-9]+){0,2})|\*)(;q=[0-1](\.[0-9]+)?)?)*/g);

        const content = strings.map(item => {
            if (!item) {
                return null;
            }

            const bits = item.split(';');
            const ietf = bits[0].split('-');
            const hasScript = ietf.length === 3;

            return {
                code: ietf[0],
                script: hasScript ? ietf[1] : null,
                region: hasScript ? ietf[2] : ietf[1],
                quality: bits[1] ? parseFloat(bits[1].split('=')[1]) : 1.0
            };
        });

        return content
            .filter(item => !!item)
            .sort((a, b) => b.quality - a.quality);
    }

    private notify(message: string) {
        if (this._config && this._config.production) {
            this.warn(message);
            return;
        }
        throw new Error(message);
    }

}
